import React from 'react';
import { useLang } from '../i18n/useLang';

const dotColors = {
  'dot-t': 'bg-nf-teal',
  'dot-v': 'bg-nf-p3',
  'dot-r': 'bg-nf-rose',
  'dot-a': 'bg-nf-amber',
  'dot-g': 'bg-nf-green',
};

const pillarConfig = [
  { n: '01', id: 'pillar-health', color: 'text-nf-teal', dotClass: 'dot-t', badgeBg: 'rgba(16,185,129,.1)', badgeColor: '#10b981' },
  { n: '02', id: 'pillar-agentic', color: 'text-nf-cyan', dotClass: 'dot-t', badgeBg: 'rgba(6,182,212,.1)', badgeColor: '#06b6d4' },
  { n: '03', id: 'pillar-ml', color: 'text-nf-p3', dotClass: 'dot-v', badgeBg: 'rgba(139,92,246,.1)', badgeColor: '#8b5cf6' },
  { n: '04', id: 'pillar-dataset', color: 'text-nf-amber', dotClass: 'dot-a', badgeBg: 'rgba(245,158,11,.1)', badgeColor: '#f59e0b' },
  { n: '05', id: 'pillar-explainability', color: 'text-nf-rose', dotClass: 'dot-r', badgeBg: 'rgba(244,63,94,.1)', badgeColor: '#fb7185' },
];

const visionCardIcons = ['🚀', '⚡', '🤝'];

function VisualDiagram({ nodes, badge }) {
  return (
    <div className="flex flex-col items-center">
      {nodes.map((node, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-2 bg-nf-bg2 border border-white/[0.06] rounded-[10px] px-4 py-3 text-[13px] w-full">
            <span className="w-2 h-2 rounded-full bg-nf-p2 shrink-0" />
            {node}
          </div>
          {i < nodes.length - 1 && <div className="w-px h-6 bg-nf-p/30" />}
        </React.Fragment>
      ))}
      <div className="w-px h-6 bg-nf-p/30" />
      <div className="text-[12px] px-3.5 py-2 rounded-lg font-semibold" style={{ background: badge.bg, color: badge.color }}>{badge.text}</div>
    </div>
  );
}

export default function Services({ navigate }) {
  const { t } = useLang();
  const pillars = pillarConfig.map((cfg, i) => {
    const tr = t('services.pillars')[i];
    return { ...cfg, ...tr, badge: { bg: cfg.badgeBg, color: cfg.badgeColor, text: tr.badgeText } };
  });
  return (
    <div>
      {/* Hero */}
      <div className="pt-[72px] sm:pt-[80px] pb-8 lg:pb-12 px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 50% 60% at 50% 30%, rgba(109,40,217,.12) 0%, transparent 65%)' }} />
        <div className="relative max-w-[1100px] mx-auto">
          <span className="sec-label">{t('services.secLabel')}</span>
          <h1 className="font-heading font-black leading-[1.05] tracking-[-0.03em] max-w-[700px] mx-auto mt-4 mb-4"
            style={{ fontSize: 'clamp(26px, 3.8vw, 52px)' }}>
            {t('services.h1')[0]}<br /><span className="text-nf-p3">{t('services.h1')[1]}</span>
          </h1>
          <p className="text-[15px] text-nf-t2 max-w-[560px] mx-auto">{t('services.heroBody')}</p>
        </div>
      </div>

      {/* Vision */}
      <section id="section-vision" className="py-16 lg:py-20 px-4 sm:px-8 lg:px-20 bg-nf-bg2 relative z-[1]">
        <div className="max-w-[1100px] mx-auto">
          <span className="sec-label">{t('services.visionLabel')}</span>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mt-8 items-start">
            <div>
              <h2 className="sec-title">{t('services.visionTitle')[0]}<br /><span className="text-nf-p3">{t('services.visionTitle')[1]}</span></h2>
              <p className="text-[15px] text-nf-t2 leading-[1.75] mb-4">{t('services.visionBody')[0]}</p>
              <p className="text-[15px] text-nf-t2 leading-[1.75] mb-8">{t('services.visionBody')[1]}</p>
              <div className="flex gap-3 flex-wrap">
                {t('services.visionPills').map(p => (
                  <span key={p} className="vpill">{p}</span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {t('services.visionCards').map((c, i) => (
                <div key={i} className="bg-nf-bg3 border border-white/[0.06] rounded-xl p-5">
                  <div className="text-[22px] mb-2">{visionCardIcons[i]}</div>
                  <h4 className="font-heading text-[15px] font-extrabold mb-1.5">{c.title}</h4>
                  <p className="text-sm text-nf-t2 leading-[1.65]">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-4 sm:px-8 lg:px-20 py-12 lg:py-16">
        <div className="max-w-[1100px] mx-auto">
          {pillars.map((p, idx) => (
            <div key={p.n} id={p.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start py-12 lg:py-16 ${idx < pillars.length - 1 ? 'border-b border-white/[0.06]' : ''}`}>
              <div className={idx % 2 === 1 ? 'order-2' : ''}>
                <div className={`font-heading text-[13px] font-black mb-1 ${p.color}`}>{t('services.pillarLabel')} {p.n}</div>
                <h3 className="font-heading text-[26px] font-black mb-4">{p.title}</h3>
                <p className="text-[15px] text-nf-t2 leading-[1.75] mb-6">{p.desc}</p>
                <ul className="flex flex-col gap-2">
                  {p.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-nf-t2">
                      <span className={`w-1.5 h-1.5 rounded-full mt-[6px] shrink-0 ${dotColors[p.dotClass]}`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`bg-nf-bg3 border border-white/[0.06] rounded-2xl p-8 ${idx % 2 === 1 ? 'order-1' : ''}`}>
                <VisualDiagram nodes={p.nodes} badge={p.badge} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="py-16 lg:py-[120px] px-4 sm:px-8 lg:px-20 text-center relative overflow-hidden bg-nf-bg2">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_50%_50%,rgba(109,40,217,0.14),transparent_70%)] pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto">
          <h2 className="font-heading font-black leading-[1.05] tracking-[-0.03em] max-w-[740px] mx-auto mb-5"
            style={{ fontSize: 'clamp(30px, 4.5vw, 58px)' }}>
            {t('services.ctaHeading')[0]}<span className="text-nf-p3">{t('services.ctaHeading')[1]}</span>
          </h2>
          <p className="text-[17px] text-nf-t2 max-w-[500px] mx-auto mb-11">{t('services.ctaBody')}</p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
            <a href="https://cal.com/willy-carlos-tchuitcheu-lz7ww7/15min?overlayCalendar=true" target="_blank" rel="noopener noreferrer" className="btn-cta btn-lg w-full sm:w-auto text-center no-underline">{t('services.ctaBtnPrimary')}</a>
            <button className="btn-outline w-full sm:w-auto" onClick={() => navigate('learn')}>{t('services.ctaBtnSecondary')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
